import React, { useEffect, useState } from 'react';
import axios from '../../api/axios';
import { 
  Button, 
  Col, 
  Form, 
  Row, 
  Table, 
  Spinner, 
  Pagination, 
  Badge,
  InputGroup,
  Modal
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
// import { FaSearch, FaPrint, FaFileExport } from 'react-icons/fa';
import { Search, Printer, FileSpreadsheet, FileEarmarkSpreadsheet, ArrowLeft } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import { useNavigate } from 'react-router-dom';
import OutletSelect from '../../components/OutletSelect';
import { MAX_DATE_RANGE_DAYS, DATE_RANGE_ERROR_MESSAGE } from '../../constants/dateConstants';

const KioskUserReport = () => {

    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);

    const navigate = useNavigate(); // Add this line
    const accessToken = localStorage.getItem('authToken');
    const [kioskUserData, setKioskUserData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(1);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState('');
    const [outlets, setOutlets] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [selectedOutletName, setSelectedOutletName] = useState('')
    const [clicked, setClicked] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFormat, setExportFormat] = useState('pdf');
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));
        
        setStartDate(start);
        setEndDate(end);
        getOutlets();
    }, []);

    useEffect(() => {
        if (clicked && searchText === "") {
            const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
            const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
            getKioskUsers(currentPage, recordsPerPage, searchText, selectedOutlet.value, adjustedStartDate, adjustedEndDate);
        }
    }, [recordsPerPage, searchText, currentPage]);

    const handleDownloadExcel = async () => {
        setDownloadBtnLoading(true);
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        getKioskUsers(currentPage, recordsPerPage, searchText, selectedOutlet.value, adjustedStartDate, adjustedEndDate, true);
    };

    const getKioskUsers = async(page, limit, search, outlet, start, end, download) => {
        try {
            setLoading(true);
            const headers = { 
                Authorization: `Bearer ${accessToken}`,
                "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            };
            let url = `kiosk-user`;

            if (outlet && outlet !== '') {
                url += `?outletId=${outlet}`;
            }

            if (search && search !== "") {
                url += `${url.includes('?') ? '&' : '?'}search=${search}`;
            } else {
                url += `${url.includes('?') ? '&' : '?'}limit=${limit}&page=${page}`;
                if (start && end) {
                    url += `&startDate=${start?.toISOString().split("T")[0]}&endDate=${end?.toISOString().split("T")[0]}`;
                }
            }

            if (download) {
                url += `&isDownload=${download}`;
            }

            const response = await axios.get(url, { 
                headers,
                responseType: download ? 'blob' : 'json'
             });

            if (download) {
                // Create a blob from the response data
                const blob = new Blob([response.data], { 
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                });
                
                // Create a link element
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                
                // Set the filename for the download
                const fileName = `${selectedOutletName} Kiosk Users Report (${start?.toISOString().split("T")[0]} - ${end?.toISOString().split("T")[0]}).xlsx`;
                link.download = fileName;
                
                // Append the link to the body and trigger the download
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
        
                toast.success('Excel file downloaded successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light"
                });
            } else {
                setKioskUserData(response?.data?.data?.kioskUsers);
                setFilteredData(response?.data?.data?.kioskUsers);
                setTotalPages(response?.data?.data?.pagination?.pages || 1);
                setLoading(false);
                toast.success(response?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored"
                });
            }
           
        } catch (error) {
            console.error("Error fetching kiosk users:", error);
            setLoading(false);
            toast.error("Failed to fetch kiosk users", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setLoading(false);
            setDownloadBtnLoading(false);
        }
    }

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });
            if (response?.data?.status === true) {
                const activeOutlets = response?.data?.data?.outlets.filter(outlet => outlet?.status === "ACTIVE" || "INACTIVE");
                setOutlets(activeOutlets);
            }
        } catch (error) {
            console.error("Error fetching outlets:", error);
            toast.error("Failed to fetch outlets", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setClicked(true);
        setCurrentPage(1);
        
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        
        await getKioskUsers(1, recordsPerPage, searchText, selectedOutlet.value, adjustedStartDate, adjustedEndDate);
        
        toast.success("Data loaded successfully", {
            position: "top-right",
            autoClose: 2000,
            theme: "colored",
        });
    }

    const handlePrintReport = () => {
        const formatDate = (date) => {
            return new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        };
        
        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);

        const tableContent = `
            <table style="border-collapse: collapse; width: 100%; font-size: 12px;">
                <thead>
                    <tr style="background-color: #f8f9fa;">
                        <th style="border: 1px solid #dee2e6; padding: 8px;">#</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">First Order Date</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Last Order Date</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Customer Name</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Mobile</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Email</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Outlet</th>
                        <th style="border: 1px solid #dee2e6; padding: 8px;">Total Orders</th>
                    </tr>
                </thead>
                <tbody>
                    ${filteredData?.length > 0 ? 
                        filteredData?.map((customer, index) => `
                            <tr>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">${index + 1}</td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.firstCreatedAt || '-'}</td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.lastCreatedAt || '-'}</td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.name || '-'}</td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.phone || '-'}</td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">${customer?.email || 'No Email'}</td>
                                <td style="border: 1px solid #dee2e6; padding: 8px;">
                                    ${customer?.outletDetails?.map(out => out?.name).filter(Boolean).join(', ') || '-'}
                                </td>
                                <td style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">${customer?.repeatCount || 0}</td>
                            </tr>
                        `).join("") :
                        `<tr>
                            <td colspan="8" style="border: 1px solid #dee2e6; padding: 8px; text-align: center;">No data available</td>
                        </tr>`
                    }
                </tbody>
            </table>
        `;
    
        const win = window.open("", "_blank");
        win.document.write(`
            <html>
                <head>
                    <title>Kiosk Users Report</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        .header { display: flex; justify-content: space-between; margin-bottom: 20px; }
                        .title { font-size: 18px; font-weight: bold; }
                        .date-range { font-size: 14px; color: #666; }
                        table { width: 100%; border-collapse: collapse; }
                        th { background-color: #f8f9fa; text-align: left; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="title">Kiosk Users Report</div>
                        <div class="date-range">${formattedStartDate} to ${formattedEndDate}</div>
                    </div>
                    ${tableContent}
                    <script>
                        setTimeout(() => {
                            window.print();
                            window.close();
                        }, 500);
                    </script>
                </body>
            </html>
        `);
        win.document.close();
    }

    const handleEndDateChange = (date) => {
        
        const start = new Date(startDate);
        const end = new Date(date);

        // Remove time part
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        const maxEndDate = new Date(start);
        maxEndDate.setDate(start.getDate() + MAX_DATE_RANGE_DAYS);

        if (maxEndDate && startDate && maxEndDate < startDate) {
            setError('End date cannot be earlier than start date');
        } else {
            setError('');
            setEndDate(date);
        }
    };

    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage, endPage;

        if (totalPages <= maxVisiblePages) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
            const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;
            
            if (currentPage <= maxVisibleBeforeCurrent) {
                startPage = 1;
                endPage = maxVisiblePages;
            } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
                startPage = totalPages - maxVisiblePages + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - maxVisibleBeforeCurrent;
                endPage = currentPage + maxVisibleAfterCurrent;
            }
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item 
                    key={i} 
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item 
                    key={totalPages} 
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
            const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
            const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
            getKioskUsers(page, recordsPerPage, searchText, selectedOutlet.value, adjustedStartDate, adjustedEndDate);
        }
    };

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${
                skin === "dark" ? "bg-dark text-light" : "bg-light"
                }`}
            >
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="mb-0 fw-bold d-inline-block">
                                        <i className="bi bi-people me-2"></i>KIOSK USERS REPORT
                                    </h4>
                                </div>
                                <div>
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        onClick={() => navigate('/reports/all-reports')}
                                        className="me-3 d-flex align-items-center fw-semibold"
                                        style={{ height: '38px' }}
                                    >
                                        <ArrowLeft className="me-1" /> Back to Reports
                                    </Button>
                                </div>
                        </div>
                        <hr className='mt-0 mb-0' /> 
                        
                        <div className="card-body">
                            {/* Filters Section */}
                            <div className="p-3 bg-light rounded-3">
                                <Form onSubmit={handleSubmit}>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group controlId="outletSelect">
                                                <OutletSelect 
                                                    value={selectedOutlet}
                                                    onChange={(option) => {
                                                        setSelectedOutlet(option);
                                                        setSelectedOutletName(option?.label);
                                                    }}
                                                    required={true}
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={2}>
                                            <Form.Group controlId="startDate">
                                                <Form.Label className="fw-semibold">
                                                    <i className="bi bi-calendar me-2"></i>Start Date
                                                </Form.Label>
                                                <DatePicker
                                                    selected={startDate}
                                                    onChange={(date) => {
                                                        setStartDate(date);
                                                        // Reset endDate if it's outside the new 60-day window
                                                        if (endDate && date && endDate > new Date(date.getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000)) {
                                                        setEndDate(null);
                                                        }
                                                    }}
                                                    dateFormat="dd-MM-yyyy"
                                                    placeholderText="Start Date"
                                                    className="form-control border-secondary"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={2}>
                                            <Form.Group controlId="endDate">
                                                <Form.Label className="fw-semibold">
                                                    <i className="bi bi-calendar me-2"></i>End Date
                                                </Form.Label>
                                                <DatePicker
                                                    selected={endDate}
                                                    onChange={(date) => {
                                                        if (startDate && date > new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000)) {
                                                        setError(DATE_RANGE_ERROR_MESSAGE);
                                                        } else {
                                                        setError("");
                                                        handleEndDateChange(date);
                                                        }
                                                    }}
                                                    dateFormat="dd-MM-yyyy"
                                                    placeholderText="End Date"
                                                    className="form-control border-secondary"
                                                    minDate={startDate}
                                                    maxDate={startDate ? new Date(startDate.getTime() + MAX_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000) : null}
                                                />
                                                {error && <div className="text-danger small mt-1">{error}</div>}
                                            </Form.Group>
                                        </Col>


                                        <Col md={2} className="d-flex align-items-end">
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                className="w-100"
                                                disabled={!selectedOutlet || !startDate || !endDate}
                                            >
                                                {loading ? (
                                                    <Spinner size="sm" animation="border" />
                                                ) : (
                                                    "Generate Report"
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </div>

                            {/* Search and Actions Section */}
                            <div className="mb-4 p-3 bg-light rounded-3">
                                <Row className="g-3 align-items-center">
                                    <Col md={6}>
                                        <InputGroup>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by name, mobile or email"
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                className="border-secondary"
                                            />
                                            <Button 
                                                variant="outline-secondary"
                                                className='w-25'
                                                onClick={handleSubmit}
                                                style={{ zIndex: 0}}
                                                disabled={!selectedOutlet || !startDate || !endDate}
                                            >
                                                <Search /> Search
                                            </Button>
                                        </InputGroup>
                                    </Col>
                                    
                                    {/* <Col md={3}>
                                        <Button
                                            variant="outline-primary"
                                            className="w-100"
                                            onClick={handleSubmit}
                                            disabled={!searchText.trim()}
                                        >
                                            <Search className="me-2" />
                                            Search
                                        </Button>
                                    </Col> */}
                                    
                                    <Col md={3}>
                                        <Button
                                            variant="outline-success"
                                            className="w-100 fw-semibold"
                                            onClick={handleDownloadExcel}
                                            disabled={!filteredData.length}
                                        >
                                            <FileEarmarkSpreadsheet className="me-2" />
                                            { downloadBtnLoading ? <Spinner size='sm' /> : 'Download Excel' }
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Results Section */}
                            {!clicked ? (
                                <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                                    <div className="text-center">
                                        <i className="bi bi-clipboard-data fs-1 text-muted"></i>
                                        <h5 className="text-muted mt-3">
                                            Please select an outlet and date range to view kiosk users
                                        </h5>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Pagination and Records Per Page - Top */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <span className="me-2 text-muted">Show</span>
                                            <Form.Select
                                                style={{ width: 'auto' }}
                                                value={recordsPerPage}
                                                onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                                className="border-secondary"
                                            >
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                                <option value={300}>300</option>
                                                <option value={500}>500</option>
                                            </Form.Select>
                                            <span className="ms-2 text-muted">entries</span>
                                        </div>
                                        
                                        <Pagination className="mb-0">
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            />
                                            {renderPaginationItems()}
                                            <Pagination.Next
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            />
                                        </Pagination>
                                    </div>

                                    {/* Kiosk Users Table */}
                                    <div className="table-responsive border rounded-3">
                                        <Table striped bordered hover className="mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th width="50">#</th>
                                                    <th width="120">First Order</th>
                                                    <th width="120">Last Order</th>
                                                    <th>Customer Name</th>
                                                    <th>Contact</th>
                                                    <th>Outlet</th>
                                                    <th width="100" className="text-center">Total Orders</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">
                                                            <Spinner animation="border" variant="primary" />
                                                            <p className="mt-2 mb-0">Loading kiosk users...</p>
                                                        </td>
                                                    </tr>
                                                ) : filteredData?.length > 0 ? (
                                                    filteredData.map((customer, index) => (
                                                        <tr key={index}>
                                                            <td className="text-muted">
                                                                {(currentPage - 1) * recordsPerPage + index + 1}
                                                            </td>
                                                            <td>
                                                                <small>{customer?.firstCreatedAt || '-'}</small>
                                                            </td>
                                                            <td>
                                                                <small>{customer?.lastCreatedAt || '-'}</small>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold">{customer?.name || '-'}</div>
                                                            </td>
                                                            <td>
                                                                <div className="text-primary">{customer?.phone || '-'}</div>
                                                                <div className="text-muted small">{customer?.email || 'No email'}</div>
                                                            </td>
                                                            <td>
                                                                <small>
                                                                    {customer?.outletDetails?.map(out => out?.name).filter(Boolean).join(', ') || '-'}
                                                                </small>
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge bg="primary" pill>
                                                                    {customer?.repeatCount || 0}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4">
                                                            <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                                                            <p className="mt-2 mb-0 text-muted">
                                                                No kiosk users found for the selected criteria
                                                            </p>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination and Records Per Page - Bottom */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="text-muted">
                                            Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
                                            {Math.min(currentPage * recordsPerPage, filteredData?.length)} of{' '}
                                            {filteredData?.length} entries
                                        </div>
                                        <Pagination>
                                            <Pagination.Prev
                                                disabled={currentPage === 1}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            />
                                            {renderPaginationItems()}
                                            <Pagination.Next
                                                disabled={currentPage === totalPages}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            />
                                        </Pagination>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Export Modal */}
                    <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Export Report</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Select Export Format</Form.Label>
                                <Form.Select 
                                    value={exportFormat}
                                    onChange={(e) => setExportFormat(e.target.value)}
                                >
                                    <option value="pdf">PDF</option>
                                    <option value="excel">Excel</option>
                                    <option value="csv">CSV</option>
                                </Form.Select>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={() => {
                                // Handle export logic here
                                setShowExportModal(false);
                            }}>
                                Export
                            </Button>
                        </Modal.Footer>
                    </Modal>
            </div>
        </React.Fragment>
    );
};

export default KioskUserReport;